<template>
  <v-container fluid class="pa-6 pa-md-8">
    <div class="page-head d-flex flex-wrap align-center justify-space-between">
      <div>
        <h1 class="page-head__title">Transactions</h1>
        <p class="page-head__sub">
          All USSD transactions across the credit union.
        </p>
      </div>
      <div class="head-actions d-flex align-center">
        <v-menu
          ref="menu"
          v-model="menu"
          :close-on-content-click="false"
          :return-value.sync="dates"
          transition="scale-transition"
          offset-y
          min-width="auto"
          :nudge-left="120"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              outlined
              v-bind="attrs"
              v-on="on"
              class="toolbar-btn text-none"
              :class="{ 'toolbar-btn--active': dates.length > 0 }"
            >
              <v-icon small left>mdi-calendar-range-outline</v-icon>
              {{ dates.length > 0 ? dateRangeText : "Date range" }}
              <v-icon
                v-if="dates.length === 2"
                small
                right
                @click.stop="clearDates"
                >mdi-close</v-icon
              >
            </v-btn>
          </template>
          <v-date-picker v-model="dates" range color="primary">
            <v-spacer></v-spacer>
            <v-btn small text color="primary" @click="menu = false">Cancel</v-btn>
            <v-btn @click="setFilteredDate" text color="primary">OK</v-btn>
          </v-date-picker>
        </v-menu>

        <v-menu offset-y transition="slide-y-transition">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              color="primary"
              depressed
              class="text-none export-btn"
              :loading="isLoading"
              v-bind="attrs"
              v-on="on"
            >
              <v-icon small left>mdi-tray-arrow-down</v-icon>
              Export
              <v-icon small right>mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="exportTransactions('xlsx')">
              <v-list-item-icon class="mr-3">
                <v-icon small>mdi-file-excel-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>Excel (.xlsx)</v-list-item-title>
            </v-list-item>
            <v-list-item @click="exportTransactions('csv')">
              <v-list-item-icon class="mr-3">
                <v-icon small>mdi-file-delimited-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-title>CSV (.csv)</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>

    <div class="toolbar d-flex align-center flex-wrap mb-4">
      <v-text-field
        outlined
        dense
        rounded
        hide-details
        prepend-inner-icon="mdi-magnify"
        placeholder="Search by account number…"
        v-model="search"
        color="primary"
        class="toolbar__search"
      />
      <v-select
        outlined
        dense
        rounded
        hide-details
        color="primary"
        prepend-inner-icon="mdi-check-decagram-outline"
        v-model="statusFilter"
        :items="statusOptions"
        class="toolbar__select"
      />
      <v-select
        outlined
        dense
        rounded
        hide-details
        color="primary"
        prepend-inner-icon="mdi-swap-vertical"
        v-model="requestTypeFilter"
        :items="typeOptions"
        class="toolbar__select"
      />
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-btn
            icon
            class="ml-1 toolbar__refresh"
            :loading="pageLoading"
            @click="fetchTransactions"
            v-on="on"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
        <span>Get latest transactions</span>
      </v-tooltip>
    </div>

    <v-data-table
      :headers="headers"
      :items="transactions"
      :loading="pageLoading"
      item-key="branch"
      :server-items-length="paginate.total"
      :options.sync="options"
      class="data-table"
      loading-text="Loading… Please wait"
      :footer-props="{ itemsPerPageOptions: [60, 80, 100] }"
    >
      <template #item.reference="{ item }">
        <div class="copy-cell" v-if="item.reference">
          <span class="tabular">{{ item.reference }}</span>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                x-small
                class="copy-cell__btn"
                v-on="on"
                @click="copy(item.reference, `ref-${rowId(item)}`)"
              >
                <v-icon x-small :color="isCopied(`ref-${rowId(item)}`) ? 'success' : ''">
                  {{ isCopied(`ref-${rowId(item)}`) ? "mdi-check" : "mdi-content-copy" }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ isCopied(`ref-${rowId(item)}`) ? "Copied!" : "Copy reference" }}</span>
          </v-tooltip>
        </div>
        <span v-else class="text-muted">—</span>
      </template>
      <template #item.accountNumber="{ item }">
        <div class="copy-cell" v-if="item.accountNumber">
          <span class="tabular">{{ item.accountNumber }}</span>
          <v-tooltip top>
            <template v-slot:activator="{ on }">
              <v-btn
                icon
                x-small
                class="copy-cell__btn"
                v-on="on"
                @click="copy(item.accountNumber, `acc-${rowId(item)}`)"
              >
                <v-icon x-small :color="isCopied(`acc-${rowId(item)}`) ? 'success' : ''">
                  {{ isCopied(`acc-${rowId(item)}`) ? "mdi-check" : "mdi-content-copy" }}
                </v-icon>
              </v-btn>
            </template>
            <span>{{ isCopied(`acc-${rowId(item)}`) ? "Copied!" : "Copy account number" }}</span>
          </v-tooltip>
        </div>
        <span v-else class="text-muted">—</span>
      </template>
      <template #item.meta="{ item }">
        <span
          v-if="item.meta && item.meta.accountName"
          class="cell-strong text-capitalize"
          >{{ item.meta.accountName }}</span
        >
        <span v-else class="text-muted">N/A</span>
      </template>
      <template #item.totalAmount="{ item }">
        <span class="tabular cell-amount"
          >GHC {{ item.totalAmount ? item.totalAmount : "N/A" }}</span
        >
      </template>
      <template #item.requestType="{ item }">
        <span class="pill pill--muted">{{
          requestTypeLabel(item.requestType)
        }}</span>
      </template>
      <template #item.service="{ item }">
        <span class="pill pill--brand text-uppercase">
          {{ item.meta.product ? item.meta.product : item.meta.name }}
        </span>
      </template>
      <template #item.ticketType="{ item }">
        <span class="text-uppercase">{{ getTicketType(item) }}</span>
      </template>
      <template #item.ticketQty="{ item }">
        <span class="tabular">{{ getTicketQty(item) }}</span>
      </template>
      <template #item.paymentStatus="{ item }">
        <span
          v-if="
            item.paymentStatus &&
              item.paymentStatus.toLowerCase() === 'success'
          "
          class="pill pill--success text-uppercase"
          >{{ item.paymentStatus }}</span
        >
        <span
          v-else-if="
            item.paymentStatus &&
              item.paymentStatus.toLowerCase() === 'failed'
          "
          class="pill pill--failed text-uppercase"
          >{{ item.paymentStatus }}</span
        >
        <span v-else class="pill pill--pending text-uppercase">{{
          item.paymentStatus ? item.paymentStatus : "Pending"
        }}</span>
      </template>
      <template #item.createdAt="{ item }">
        <span class="text-muted">{{ item.createdAt | dateFormat }}</span>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import { debounce } from "lodash";
import moment from "moment";
export default {
  name: "Transactions",

  data() {
    return {
      search: "",
      copiedKey: null,
      statusFilter: null,
      requestTypeFilter: null,
      options: {
        page: 1,
        itemsPerPage: 60
      },
      menu: false,
      dates: [],
      chosenDateRange: [],
      statusOptions: [
        { text: "All statuses", value: null },
        { text: "Success", value: "COMPLETED" },
        { text: "Pending", value: "PENDING" },
        { text: "Failed", value: "FAILED" }
      ],
      typeOptions: [
        { text: "All types", value: null },
        { text: "Deposit", value: "ACCOUNT_DEPOSIT" },
        { text: "Withdrawal", value: "WITHDRAWAL" }
      ]
    };
  },

  computed: {
    ...mapGetters({
      pageLoading: "getIsPageLoading",
      isLoading: "getIsLoading",
      paginate: "transactions/getPagination",
      transactions: "transactions/getAllTransactions",
      profile: "auth/getUserDetails"
    }),
    dateRangeText() {
      return this.dates.join(" ~ ");
    },
    isTicketingOrg() {
      return this.profile?.organizationType === "TICKETS";
    },

    headers() {
      return [
        {
          text: "Date",
          value: "createdAt",
          filterable: false,
          sortable: false
        },
        {
          text: "Ref#",
          value: "reference",
          filterable: false,
          sortable: false
        },
        {
          text: "Acc. Number",
          value: "accountNumber",
          filterable: true,
          sortable: false
        },
        {
          text: "Acc. Name",
          value: "meta",
          filterable: true,
          sortable: false
        },
        {
          text: "Amount (GHS)",
          value: "totalAmount",
          filterable: true,
          sortable: false
        },
        {
          text: "Type",
          value: "requestType",
          sortable: false
        },
        {
          text: "Service",
          value: "service",
          sortable: false
        },
        ...(this.isTicketingOrg
          ? [
              {
                text: "Ticket Type",
                value: "ticketType",
                sortable: false
              },
              {
                text: "Qty",
                value: "ticketQty",
                sortable: false
              }
            ]
          : []),
        {
          text: "Status",
          value: "paymentStatus",
          sortable: false
        }
      ];
    }
  },
  filters: {
    dateFormat(value) {
      return moment(value).format("Do MMM,YYYY HH:mm:ss");
    }
  },

  watch: {
    search() {
      this.resetAndFetch();
    },
    statusFilter() {
      this.resetAndFetch();
    },
    requestTypeFilter() {
      this.resetAndFetch();
    },
    chosenDateRange() {
      this.resetAndFetch();
    },
    options: {
      handler() {
        this.fetchTransactions();
      },
      deep: true
    }
  },

  methods: {
    buildQuery() {
      const { page, itemsPerPage } = this.options;
      const params = new URLSearchParams();
      params.set("page", page || 1);
      params.set("size", itemsPerPage || 60);
      if (this.search) params.set("searchTerm", this.search);
      if (this.statusFilter) params.set("status", this.statusFilter);
      if (this.requestTypeFilter)
        params.set("requestType", this.requestTypeFilter);
      if (this.chosenDateRange.length === 2) {
        const [startDate, endDate] = this.chosenDateRange;
        params.set("startDate", startDate);
        params.set("endDate", endDate);
      }
      return `?${params.toString()}`;
    },
    // Debounced so the several watchers that can fire together (e.g. a filter
    // change that also resets the page) collapse into a single request.
    fetchTransactions: debounce(function() {
      const query = this.buildQuery();
      if (this.search) {
        this.$store.dispatch("transactions/searchList", query);
      } else {
        this.$store.dispatch("transactions/list", query);
      }
    }, 300),
    resetAndFetch() {
      this.options.page = 1;
      this.fetchTransactions();
    },
    setFilteredDate() {
      this.$refs.menu.save(this.dates);
      // v-date-picker returns the range in selection order — sort so start<=end.
      this.chosenDateRange = [...this.dates].sort();
    },
    clearDates() {
      this.dates = [];
      this.chosenDateRange = [];
    },
    requestTypeLabel(type) {
      const map = {
        ACCOUNT_DEPOSIT: "Deposit",
        WITHDRAWAL: "Withdrawal",
        AIRTIME: "Airtime",
        DATA: "Data"
      };
      return map[type] || type || "—";
    },
    exportTransactions(ext) {
      // Always bound exports to a date range; default to the last 30 days when
      // none is selected so we never trigger an unbounded download.
      let startDate, endDate;
      if (this.chosenDateRange.length === 2) {
        [startDate, endDate] = this.chosenDateRange;
      } else {
        endDate = moment().format("YYYY-MM-DD");
        startDate = moment()
          .subtract(30, "days")
          .format("YYYY-MM-DD");
        this.$store.dispatch(
          "showSnackBar",
          {
            snackBar: true,
            message: "No date range selected — exporting the last 30 days.",
            status: "info"
          },
          { root: true }
        );
      }
      const filterPart = [this.statusFilter, this.requestTypeFilter]
        .filter(Boolean)
        .join("-");
      const fileName = `transactions-${startDate}_${endDate}${
        filterPart ? "-" + filterPart : ""
      }`;
      this.$store.dispatch("transactions/download", {
        fileName,
        ext,
        startDate,
        endDate,
        status: this.statusFilter,
        requestType: this.requestTypeFilter,
        searchTerm: this.search || undefined
      });
    },
    rowId(item) {
      return item._id || item.reference;
    },
    isCopied(key) {
      return this.copiedKey === key;
    },
    async copy(text, key) {
      if (text === null || text === undefined) return;
      const ok = await this.writeToClipboard(String(text));
      if (ok) {
        this.copiedKey = key;
        clearTimeout(this._copyTimer);
        this._copyTimer = setTimeout(() => {
          this.copiedKey = null;
        }, 1500);
      } else {
        this.$store.dispatch(
          "showSnackBar",
          {
            snackBar: true,
            message: "Couldn't copy to clipboard.",
            status: "red"
          },
          { root: true }
        );
      }
    },
    async writeToClipboard(value) {
      // Prefer the async Clipboard API; fall back to execCommand when it's
      // unavailable or blocked (e.g. permissions / focus), which still works
      // within a user gesture like a click.
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(value);
          return true;
        }
      } catch (e) {
        // fall through to legacy path
      }
      try {
        const el = document.createElement("textarea");
        el.value = value;
        el.style.position = "fixed";
        el.style.top = "-9999px";
        el.style.opacity = "0";
        document.body.appendChild(el);
        el.focus();
        el.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(el);
        return ok;
      } catch (e) {
        return false;
      }
    },
    getTicketType(item) {
      return item?.meta?.label?.key?.label ?? "-";
    },
    getTicketQty(item) {
      return item?.meta?.label?.quantity ?? "-";
    }
  },
  created() {
    this.fetchTransactions();
  }
};
</script>

<style scoped>
.head-actions {
  gap: 8px;
}
.toolbar {
  gap: 8px;
}
.toolbar__search {
  max-width: 360px;
  flex: 1 1 240px;
}
.toolbar__select {
  max-width: 190px;
  flex: 0 0 auto;
}
.toolbar__refresh {
  color: var(--muted-foreground);
}
.toolbar-btn {
  border-color: var(--border) !important;
  color: var(--muted-foreground) !important;
  border-radius: var(--radius) !important;
  height: 40px !important;
  font-weight: 500;
}
.toolbar-btn--active {
  border-color: var(--signal) !important;
  color: var(--signal) !important;
}
.export-btn {
  border-radius: var(--radius) !important;
  height: 40px !important;
  font-weight: 600;
}
.cell-strong {
  font-weight: 600;
  color: var(--foreground);
}
.cell-amount {
  font-weight: 600;
  color: var(--foreground);
}
.copy-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.copy-cell__btn {
  color: var(--muted-foreground) !important;
  opacity: 0;
  transition: opacity 0.12s ease;
}
.copy-cell:hover .copy-cell__btn,
.copy-cell__btn:focus {
  opacity: 1;
}
.text-muted {
  color: var(--muted-foreground);
}
</style>
